import { useState } from 'react'
import { Image, Save, Palette, Upload, X, CheckCircle2, AlertTriangle } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { generateId, getStatusColor } from '../utils/helpers'

const imageTypes = ['主图1-8', 'A+模块', '高级A+', '品牌故事', '视频关键帧', '场景图', '参数图', '兼容图', '对比图', '安全认证图', '爆炸图', '安装步骤图']

const chargerRules = [
  '接口不能跑偏', 'USB-C / 圆口 / 插头必须正确', '电源主体、插头、线材连接符合物理规律',
  '不能出现多根错误线材', '不能出现不支持的品牌Logo', '不能出现虚假认证',
  '不能出现错误规格', '不能出现多余配件', '标签文字不能乱写', '插头、线材、适配器比例合理'
]

export default function VisualWorkbench() {
  const { agents, prompts, knowledgeBases, tasks, setTasks, visualBatchTasks, canExecute } = useApp()
  const visualAgents = agents.filter(a => a.module === 'Visual' && (a.status === '主用' || a.status === '测试'))
  const [agentId, setAgentId] = useState('')
  const [productInfo, setProductInfo] = useState('')
  const [imageReq, setImageReq] = useState('')
  const [selectedTypes, setSelectedTypes] = useState(['主图1-8'])
  const [materials, setMaterials] = useState([])
  const [output, setOutput] = useState('')
  const [loading, setLoading] = useState(false)

  const agent = agents.find(a => a.id === agentId)

  const toggleType = (t) => setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])

  const handleMaterialUpload = (e) => {
    const files = Array.from(e.target.files)
    setMaterials([...materials, ...files.map(f => ({ id: generateId(), name: f.name, size: f.size }))])
  }

  const execute = () => {
    if (!canExecute()) return alert('无权限')
    if (!agentId) return alert('请选择Agent')
    if (!productInfo) return alert('请输入产品信息')
    setLoading(true)
    setTimeout(() => {
      setOutput(`【批量视觉任务生成结果】\n\n产品: ${productInfo}\n风格: ${imageReq || '默认'}\n选中类型: ${selectedTypes.join(', ')}\n\n=== 任务列表 ===\n${selectedTypes.map((t, i) => `${i+1}. [${t}] ${productInfo} - ${imageReq} style, professional product photography, clean background`).join('\n')}\n\n=== Midjourney Prompt 示例 (主图1) ===\nProduct photography of ${productInfo}, ${imageReq}, clean white background, soft studio lighting, professional commercial shot, 8k ultra detailed, sharp focus, cinematic --ar 1:1 --v 6 --s 750\n\n=== 3C充电器质检规则 ===\n${chargerRules.map(r => `- ${r}`).join('\n')}\n\n=== 批量任务队列预览 ===\n任务名: ${productInfo.substring(0,20)} 视觉包\n图片数量: ${selectedTypes.length * 2}\n状态: 待生成`)
      setLoading(false)
    }, 1500)
  }

  const save = () => {
    const newTask = { id: generateId(), name: `${productInfo.substring(0,20)} 视觉Prompt`, type: 'Visual',
      agentId, promptVersion: prompts.find(p => p.id === agent?.promptId)?.version || '-',
      knowledgeVersion: knowledgeBases.filter(k => agent?.knowledgeIds?.includes(k.id)).map(k => k.version).join(', ') || '-',
      model: agent?.defaultModel || '-',
      inputFiles: [], inputText: `产品: ${productInfo}\n要求: ${imageReq}\n类型: ${selectedTypes.join(', ')}`,
      aiOutput: output, humanEdit: null, adopted: false, reviewStatus: '待审核',
      owner: 'Leo', createdAt: new Date().toISOString() }
    setTasks([newTask, ...tasks]); alert('已保存')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Image size={24} className="text-pink-600" />
        <h2 className="text-xl font-bold text-slate-800">视觉 Prompt 台</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">选择视觉 Agent</label>
            <select className="select" value={agentId} onChange={e => setAgentId(e.target.value)}>
              <option value="">请选择...</option>
              {visualAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">上传资料</label>
            <div className="flex gap-2">
              <input type="file" multiple accept="image/*" className="hidden" id="visual-materials" onChange={handleMaterialUpload} />
              <label htmlFor="visual-materials" className="btn-secondary text-sm cursor-pointer flex items-center gap-1"><Upload size={14} /> 上传图片/资料</label>
            </div>
            {materials.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {materials.map(m => (
                  <span key={m.id} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">
                    {m.name} <button onClick={() => setMaterials(materials.filter(x => x.id !== m.id))}><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">批量任务类型</label>
            <div className="flex gap-2 flex-wrap">
              {imageTypes.map(t => (
                <button key={t} onClick={() => toggleType(t)} className={`px-2 py-1 rounded-lg text-xs ${selectedTypes.includes(t) ? 'bg-pink-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">产品信息</label>
            <input className="input" value={productInfo} onChange={e => setProductInfo(e.target.value)} placeholder="例如：智能手表，黑色金属表带，1.5英寸AMOLED屏幕" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">图片要求 / 风格</label>
            <textarea className="textarea h-24" value={imageReq} onChange={e => setImageReq(e.target.value)} placeholder="科技感、简约、高端商务、运动风格..." />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-xs font-medium text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle size={12} /> 3C充电器视觉质检规则（自动检查）</div>
            <div className="flex flex-wrap gap-1">
              {chargerRules.map(r => (
                <span key={r} className="text-[10px] px-1.5 py-0.5 bg-white text-amber-700 rounded border border-amber-200">{r}</span>
              ))}
            </div>
          </div>

          <button onClick={execute} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            <Palette size={16} /> {loading ? '生成中...' : '生成批量视觉任务'}
          </button>
        </div>

        <div className="space-y-4">
          {agent && (
            <div className="card p-4 bg-pink-50 border-pink-200">
              <div className="text-sm text-pink-800 font-medium mb-1">当前Agent: {agent.name}</div>
              <div className="text-xs text-pink-600">Prompt: {prompts.find(p => p.id === agent.promptId)?.name || '无'}</div>
            </div>
          )}

          {output && (
            <div className="card p-5">
              <h3 className="font-semibold text-slate-800 mb-3">生成的 Prompt & 任务表</h3>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap font-mono min-h-[200px]">{output}</div>
              <div className="flex gap-3 mt-4"><button onClick={save} className="btn-primary flex items-center gap-2"><Save size={14} /> 保存结果</button></div>
            </div>
          )}

          {visualBatchTasks.length > 0 && (
            <div className="card p-4">
              <h3 className="font-semibold text-slate-800 mb-3 text-sm">批量作图任务队列</h3>
              <div className="space-y-2">
                {visualBatchTasks.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <div className="font-medium">{t.taskName}</div>
                      <div className="text-xs text-slate-500">{t.imageCount} 张 · {t.model}</div>
                    </div>
                    <span className={`badge text-[10px] ${getStatusColor(t.status)}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
